import { useState, useEffect, useCallback } from 'react';
import { getCustomerList } from '../services/api';

const DEBOUNCE_MS = 300;

/**
 * Reusable hook for guest/customer search with debounce
 * @param {Object} options
 * @param {string} [options.searchInput] - Search query
 * @param {boolean} [options.enabled=true] - Whether to run search (e.g. when booking exists)
 * @returns {{ options: Array, loading: boolean, setSearchInput: Function }}
 */
export function useGuestSearch({ searchInput = '', enabled = true } = {}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(async (signal) => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await getCustomerList(
        {
          q: (searchInput || '').trim() || undefined,
          per_page: 25,
          page: 1,
        },
        { signal }
      );
      if (!signal?.aborted) setOptions(res?.Data ?? []);
    } catch (err) {
      if (!signal?.aborted && err?.name !== 'AbortError') setOptions([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [searchInput, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const timer = setTimeout(() => fetchOptions(controller.signal), DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [fetchOptions, enabled]);

  return { options, loading };
}
